import React, { useEffect, useMemo } from "react";
import { MilestoneSubStatusColor } from "../TicketColumns/Status";
import IconToolTip from "../../../common/IconToolTip";

const RenderMilestones = ({ defaultMilestone, milestones, selectedMilestones, setSelectedMilestones, buttonClassName = 'py-2 px-4', agents, showAgentsOnHover = false }) => {

    useEffect(() => {
        if (defaultMilestone) {
            setSelectedMilestones(defaultMilestone);
        } else if (milestones && milestones.length > 0) {
            setSelectedMilestones(milestones[0].id);
        }
    }, [setSelectedMilestones, defaultMilestone, milestones]);

    // Process milestones to add occurrence numbers for duplicate types/names
    const processedMilestones = useMemo(() => {
        if (!milestones) return [];
        const counts = {};
        const current = {};

        // Count total occurrences of each name
        milestones.forEach(m => {
            const name = m.type || m.label || 'Milestone';
            counts[name] = (counts[name] || 0) + 1;
        });

        // Append index if there are duplicates
        return milestones.map(m => {
            const name = m.type || m.label || 'Milestone';
            if (counts[name] > 1) {
                current[name] = (current[name] || 0) + 1;
                return { ...m, _displayName: `${name} ${current[name]}` };
            }
            return { ...m, _displayName: name };
        });
    }, [milestones]);

    const currentAgents = useMemo(() => {
        if (!agents || !Array.isArray(agents) || !selectedMilestones) return { sp: null, fe: null };
        const sp = agents.find((a) => a.role === 'SP' && a.milestone_id === selectedMilestones);
        const fe = agents.find((a) => a.role === 'FE' && a.milestone_id === selectedMilestones);
        return { sp, fe };
    }, [agents, selectedMilestones]);

    // Add safety check for milestones
    if (!milestones || milestones.length === 0) {
        console.log('RenderMilestones: No milestones to render');
        return null;
    }

    return (
        <div className="flex flex-col gap-4 py-1">
            <div className="flex flex-wrap gap-3">
                {processedMilestones.map((milestone, index) => {
                    const isSelected = selectedMilestones === milestone.id;
                    const statusClass = MilestoneSubStatusColor[milestone.milestone_status] || '';
                    const milestoneKey = milestone.id || milestone.value || index;

                    const milestoneBtn = (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedMilestones(milestone.id)
                            }}
                            className={`flex items-center gap-2
                                rounded-lg text-left transition-all duration-200 ease-in-out border
                                focus:ring-offset-2
                                ${isSelected
                                    ? 'bg-scogoprimary text-white shadow-md outline-none border-scogoprimary border-l-4'
                                    : `${statusClass} hover:bg-opacity-80 hover:shadow-sm`
                                } ${buttonClassName} !text-sm !font-medium`}
                        >
                            <div className="flex-shrink-0 flex items-center">
                                {isSelected ? (
                                    <span className="material-icons text-[16px] leading-none">check_circle</span>
                                ) : (
                                    <span className="material-icons text-[16px] leading-none opacity-40">radio_button_unchecked</span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 whitespace-nowrap">
                                <span className="capitalize">{milestone._displayName}</span>
                                {milestone.milestone_status && (
                                    <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-sm ${isSelected ? 'bg-white/20 text-white' : 'opacity-80'}`}>
                                        {milestone.milestone_status.replace(/_/g, ' ')}
                                    </span>
                                )}
                            </div>
                        </button>
                    );

                    if (showAgentsOnHover && agents && Array.isArray(agents)) {
                        const mSp = agents.find((a) => a.role === 'SP' && a.milestone_id === milestone.id);
                        const mFe = agents.find((a) => a.role === 'FE' && a.milestone_id === milestone.id);
                        const mSpName = mSp?.agent?.display_name || mSp?.agent?.full_name;
                        const mFeName = mFe?.agent?.display_name || mFe?.agent?.full_name;
                        let tooltip;
                        if (!mSp && !mFe) {
                            tooltip = <span>SP/FE: <strong>Not Assigned</strong></span>;
                        } else if (mSp && mFe && mSp.agent?.id === mFe.agent?.id) {
                            tooltip = <span>Current SP/FE: <strong>{mSpName}</strong></span>;
                        } else {
                            tooltip = (
                                <span>
                                    {mSp ? <>Current SP: <strong>{mSpName}</strong></> : <>SP: <strong>Not Assigned</strong></>}
                                    <br />
                                    {mFe ? <>Current FE: <strong>{mFeName}</strong></> : <>FE: <strong>Not Assigned</strong></>}
                                </span>
                            );
                        }
                        return <IconToolTip key={milestoneKey} title={tooltip}>{milestoneBtn}</IconToolTip>;
                    }

                    return <React.Fragment key={milestoneKey}>{milestoneBtn}</React.Fragment>;
                })}
            </div>
            {!showAgentsOnHover && agents && Array.isArray(agents) && (() => {
                const spName = currentAgents.sp?.agent?.display_name || currentAgents.sp?.agent?.full_name;
                const feName = currentAgents.fe?.agent?.display_name || currentAgents.fe?.agent?.full_name;
                const isSamePerson = currentAgents.sp && currentAgents.fe && currentAgents.sp.agent?.id === currentAgents.fe.agent?.id;

                if (!currentAgents.sp && !currentAgents.fe) {
                    return (
                        <div className="flex flex-wrap gap-3">
                            <div className="p-2 bg-gray-100 border-l-4 border-gray-400 text-gray-600 rounded-r-md">
                                <span className="font-bold">SP/FE:</span> Not Assigned
                            </div>
                        </div>
                    );
                }

                if (isSamePerson) {
                    return (
                        <div className="flex flex-wrap gap-3">
                            <div className="p-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded-r-md">
                                <span className="font-bold">Current SP/FE:</span> {spName}
                            </div>
                        </div>
                    );
                }

                return (
                    <div className="flex flex-wrap gap-3">
                        <div className={`p-2 ${currentAgents.sp ? 'bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700' : 'bg-gray-100 border-l-4 border-gray-400 text-gray-600'} rounded-r-md`}>
                            <span className="font-bold">Current SP:</span> {currentAgents.sp ? spName : 'Not Assigned'}
                        </div>
                        <div className={`p-2 ${currentAgents.fe ? 'bg-blue-100 border-l-4 border-blue-500 text-blue-700' : 'bg-gray-100 border-l-4 border-gray-400 text-gray-600'} rounded-r-md`}>
                            <span className="font-bold">Current FE:</span> {currentAgents.fe ? feName : 'Not Assigned'}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

export default RenderMilestones;
